import { useEffect, useReducer, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { store } from "Store";
import { resetPasswordRuleReducer, RESET_PASSWORD_RULE_ACTIONS } from "Containers/Private/UserProfile/ResetPassword/ResetPasswordRuleReducer";
import { formInfoReducer, ACTIONS } from "Containers/Private/UserProfile/ResetPassword/ResetPasswordFormDataReducer";
import PasswordRuleChecker from "Components/PasswordRuleChecker/PasswordRuleChecker";
import { Card, Badge, Stack, Form, Row, Col } from "react-bootstrap";
import ErrorIcon from '@mui/icons-material/ErrorOutlined';
import { getGeneratePWD, validateToken, getResetPWDUserInfo, submitResetPassword, validateResetPassword } from "./ForgotPasswordAxiosFuncs";
import { STATUSES, ERROR_MESSAGES } from "Utils/constants";
import { setGlobalErrorStatus } from 'Components/StatusHandling';
import { PageInfo } from "Components/PageInformation/PageInformation";
import { TextButton } from "Components/Button";
import { FormTextInput } from "Components/Forms/FormInput";
import PasswordInput from "Containers/Private/UserProfile/Component/PasswordInput";
import PasswordStrengthMeter from "Components/PasswordStrengthMeter/PasswordStrengthMeter";
import StatusOverlay from "../../Components/StatusOverlay/StatusOverlay";
import { PATHS } from "Utils/PagePaths";

// field errors key mapping
const nameConverter = {
  "password": "password",
  "newPassword": "newPassword",
  "confirmPassword": "conNewPassword"
}

const ForgotPasswordResetPassword = () => {
  const loginState = store.getState().loginInfo;
  const { token } = useParams();
  const location = useLocation();
  const isForward = location.state?.isForward;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const initialState = {
    data: '',
    loading: true,
    error: null,
  };
  const passwordTypesList = [
    "Uppercase character",
    "Lowercase character",
    "Number",
    "Valid Special characters: ! @ # $ % & * ( ) _ = + - [ ] ?"
  ];

  const [globalErrorList, setGlobalErrorList] = useState([]);
  const [initialPasswordState, setInitialPasswordState] = useState(null);
  const [formInfo, formInfoDispatch] = useReducer(formInfoReducer, initialState);
  const copyButtonRef = useRef(null);
  const [showCopyTooltip, setShowCopyTooltip] = useState(false);
  const [copyTooltipStatus, setCopyTooltipStatus] = useState('default');
  const [copyTooltipStatusMessage, setCopyTooltipStatusMessage] = useState("");
  const [resetPasswordRule, resetPasswordRuleDispatch] = useReducer(resetPasswordRuleReducer, {
    lengthCheck: false,
    // 3 of 4 checkers 
    characterCheck: {
      uppercaseCheck: false,
      lowercaseCheck: false,
      numberCheck: false,
      //: ! @ # $ % & * ( ) _ = + - [ ] ?
      specialCharCheck: false,
      hasSpecialCharCheck: false
    },
    idNameCheck: false,
    historyCheck: false,
    countLimitCheck: true
  });

  // form field config
  const formFieldDisplay = {
    password: {
      type: 'text',
      id: 'password',
      name: 'password',
      display: {
        label: 'Generate Password',
        description: "",
        title: "",
      },
      hideFieldOrgTypes: [],
      InputRef: useRef(null)
    },
    newPassword: {
      type: 'text',
      id: 'newPassword',
      name: 'newPassword',
      display: {
        label: 'New Password',
        description: "",
        title: "",
      },
      hideFieldOrgTypes: [],
      InputRef: useRef(null)
    },
    conNewPassword: {
      type: 'text',
      id: 'conNewPassword',
      name: 'conNewPassword',
      display: {
        label: 'Confirm New Password',
        description: "",
        title: "",
      },
      hideFieldOrgTypes: [],
      InputRef: useRef(null)
    }
  }

  // get value by key from res
  function getFieldValue (fieldName, data) {
    var values = data[fieldName];
    var dispValues = formFieldDisplay[fieldName];

    if (formInfo.loading) {
      return '';
    }
    else {
      var type = dispValues.type;
      switch (type) {

        case "text":
          if (values.value != null) {
            return values.value;
          }
          return "";
      }
    }
  }

  // display error after api call
  const dispErrors = (fieldName) => {
    var data = formInfo.data[fieldName];
    var dispData = formFieldDisplay[fieldName];
    return (
      <div>
        {data.validation.errors.length > 0 ? data.validation.errors.map((errorText, index) =>
          <Stack key={dispData.name + "Errors_" + index} direction="horizontal" gap={2} className="align-items-start">
            <div id="errorIcon"><ErrorIcon fontSize="small"></ErrorIcon></div>
            <div>{errorText}</div>
          </Stack>

        ) : null}
      </div>
    );
  }

  // display label config
  const dispLabel = (fieldName, isHidden, getColLabelSizes, alignment, multi) => {

    var data = formInfo.data[fieldName];
    var dispData = formFieldDisplay[fieldName];

    //console.log("print out data, dispData, fieldName", data, dispData, fieldName);

    if (alignment == Row) {
      if (multi == true) {
        return (
          <Col hidden={isHidden}
            xs={getColLabelSizes('xs')}
            sm={getColLabelSizes('sm')}
            md={getColLabelSizes('md')}
            lg={getColLabelSizes('lg')}
            xl={getColLabelSizes('xl')}
            xxl={getColLabelSizes('xxl')}
          >
            <legend>
              {<span className="me-2">{dispData.display.label}</span>}
              {data.validation.required ? '' : <Badge bg="optional">Optional</Badge>}
            </legend>
          </Col>
        );
      }
      else {
        return (
          <Form.Label visuallyHidden={isHidden} column
            xs={getColLabelSizes('xs')}
            sm={getColLabelSizes('sm')}
            md={getColLabelSizes('md')}
            lg={getColLabelSizes('lg')}
            xl={getColLabelSizes('xl')}
            xxl={getColLabelSizes('xxl')}
          >
            {<span className="me-2">{dispData.display.label}</span>}
            {data.validation.required ? '' : <Badge bg="optional">Optional</Badge>}
          </Form.Label>
        );
      }
    }
    else {
      if (multi == true) {
        return (
          <Row hidden={isHidden}
            xs={getColLabelSizes('xs')}
            sm={getColLabelSizes('sm')}
            md={getColLabelSizes('md')}
            lg={getColLabelSizes('lg')}
            xl={getColLabelSizes('xl')}
            xxl={getColLabelSizes('xxl')}
          >
            <legend>
              {dispData.display.label}
              {data.validation.required ? '' : <Badge bg="optional">Optional</Badge>}
            </legend>
          </Row>
        );
      }
      else {
        return (
          <Form.Label visuallyHidden={isHidden}
            xs={getColLabelSizes('xs')}
            sm={getColLabelSizes('sm')}
            md={getColLabelSizes('md')}
            lg={getColLabelSizes('lg')}
            xl={getColLabelSizes('xl')}
            xxl={getColLabelSizes('xxl')}
          >
            {dispData.display.label}
            {data.validation.required ? '' : /*<span className="optionalField"><br></br>optional</span>*/ <Badge bg="optional">Optional</Badge>}
          </Form.Label>
        );
      }

    }
  };

  function addErrorMessages (fieldName, errorFieldName, data, isFirst) {
    //console.log("addErrorMessages", fieldName, errorFieldName, data);
    var d = data.fieldErrors;
    var values = d[errorFieldName];
    if (values != null) {
      var field = formFieldDisplay[fieldName];
      var type = field.type;
      var err = values.errorMessages;

      if (isFirst == true) {
        (field.InputRef).current.focus();
      }

      // do not display the first error summary item of "newPassword"
      if (errorFieldName === "newPassword") {
        if (err.length > 1 && err[0].includes("Password does not meet these rules")) {
          err.shift();
        }
      }

      switch (type) {
        case "text":
          formInfoDispatch({
            type: ACTIONS.TEXT_SET_ERRORS,
            name: fieldName,
            errors: err
          })
          break;
      }
    }
  }

  function clearErrorMessages (data) {
    for (const [key, value] of Object.entries(data)) {
      var t = formFieldDisplay[key];
      var fieldName = key;
      switch (t.type) {
        case "text":
          formInfoDispatch({
            type: ACTIONS.TEXT_REMOVE_ERRORS,
            name: fieldName
          })
          break;
      }
    }
  }

  // contains 3 of the 4 character types
  const checkCharacterTypes = () => {
    //console.log("checkCharacterTypes", resetPasswordRule);
    let i = 0;
    for (const key in resetPasswordRule.characterCheck) {

      if (key === "hasSepcialCharCheck") {
        continue;
      }


      if (key !== "specialCharCheck" && resetPasswordRule.characterCheck[key] === true) {
        i++;
      }

      if (key === "specialCharCheck") {
        if (resetPasswordRule.characterCheck[key] === false) {
          return false;
        } else if (resetPasswordRule.characterCheck[key] === true && resetPasswordRule.characterCheck["hasSepcialCharCheck"] === true) {
          i++;
        }
      }
    }

    if (i >= 3) {
      return true;
    }

    return false;
  }

  //check password rules
  const checkPasswordRule = (value) => {
    /**
     * state structure
    {
      lengthCheck: false,
      // 3 of 4 checkers 
      characterCheck: {
        uppercaseCheck: false,
        lowercaseCheck: false,
        numberCheck: false,
        //: ! @ # $ % & * ( ) _ = + - [ ] ?
        specialCharCheck: false,
        hasSpecialCharCheck:false
      },
      idNameCheck: false,
      historyCheck: false,
      countLimitCheck: true
    }
    */

    let lengthCheck = false;
    let characterCheck = true;
    let idNameCheck = true;
    // "changedToday"
    let countLimitCheck = !initialPasswordState?.changedToday;
    //console.log("countLimitCheck", countLimitCheck, initialPasswordState, resetPasswordRule);
    let characterCheckValidCount = 0;

    // empty check then reset
    if (value === "") {
      resetPasswordRuleDispatch({
        type: RESET_PASSWORD_RULE_ACTIONS.SET_INITIAL_DATA,
        name: null,
        value: null
      });

      resetPasswordRuleDispatch({
        type: RESET_PASSWORD_RULE_ACTIONS.CHECK_DAILY_CHANGE_COUNT,
        name: "countLimitCheck",
        value: !initialPasswordState?.changedToday
      });

      return false;
    }


    // length check
    if (value.length >= 16 && value.length <= 24) {
      lengthCheck = true;

      resetPasswordRuleDispatch({
        type: RESET_PASSWORD_RULE_ACTIONS.CHECK_LENGTH,
        name: "lengthCheck",
        value: true
      });
    } else {
      resetPasswordRuleDispatch({
        type: RESET_PASSWORD_RULE_ACTIONS.CHECK_LENGTH,
        name: "lengthCheck",
        value: false
      });
    }

    // uppercase check
    let uppercase = /[A-Z]/;
    if (uppercase.test(value)) {

      characterCheckValidCount++;

      resetPasswordRuleDispatch({
        type: RESET_PASSWORD_RULE_ACTIONS.CHECK_UPPER_CASE,
        name: "uppercaseCheck",
        value: true
      });
    } else {
      resetPasswordRuleDispatch({
        type: RESET_PASSWORD_RULE_ACTIONS.CHECK_UPPER_CASE,
        name: "uppercaseCheck",
        value: false
      });
    }

    // lowercase check
    let lowercase = /[a-z]/;
    if (lowercase.test(value)) {

      characterCheckValidCount++

      resetPasswordRuleDispatch({
        type: RESET_PASSWORD_RULE_ACTIONS.CHECK_LOWER_CASE,
        name: "lowercaseCheck",
        value: true
      });
    } else {
      resetPasswordRuleDispatch({
        type: RESET_PASSWORD_RULE_ACTIONS.CHECK_LOWER_CASE,
        name: "lowercaseCheck",
        value: false
      });
    }

    // number check
    let numberChecker = /\d/;
    if (numberChecker.test(value)) {

      characterCheckValidCount++;

      resetPasswordRuleDispatch({
        type: RESET_PASSWORD_RULE_ACTIONS.CHECK_NUMBER,
        name: "numberCheck",
        value: true
      });

    } else {
      resetPasswordRuleDispatch({
        type: RESET_PASSWORD_RULE_ACTIONS.CHECK_NUMBER,
        name: "numberCheck",
        value: false
      });
    }

    // special character check
    let specialCharCheck = "@!#$%&*()_+\-=\[\]?";
    let specialCharFlag = true;
    let hasSpecialCharFlag = false;
    for (let i = 0; i < value.length; i++) {
      let cur = value.charAt(i);

      if (cur >= '0' && cur <= '9') {
        continue;
      }

      if (cur >= 'a' && cur <= 'z') {
        continue;
      }

      if (cur >= 'A' && cur <= 'Z') {
        continue;
      }

      if (!specialCharCheck.includes(value.charAt(i))) {
        specialCharFlag = false;
        resetPasswordRuleDispatch({
          type: RESET_PASSWORD_RULE_ACTIONS.CHECK_SPECIAL_CHARACTER,
          name: "specialCharCheck",
          value: false
        });
        break;
      }

      if (specialCharCheck.includes(value.charAt(i))) {
        hasSpecialCharFlag = true;
      }
    }

    if (specialCharFlag === true) {
      resetPasswordRuleDispatch({
        type: RESET_PASSWORD_RULE_ACTIONS.CHECK_SPECIAL_CHARACTER,
        name: "specialCharCheck",
        value: true
      });
    }

    if (hasSpecialCharFlag) {
      characterCheckValidCount++;
      resetPasswordRuleDispatch({
        type: RESET_PASSWORD_RULE_ACTIONS.CHECK_HAS_SPECIAL_CHARACTER,
        name: "hasSpecialCharCheck",
        value: true
      });
    } else {
      resetPasswordRuleDispatch({
        type: RESET_PASSWORD_RULE_ACTIONS.CHECK_HAS_SPECIAL_CHARACTER,
        name: "hasSpecialCharCheck",
        value: false
      });
    }

    //User Email, First Name, Last Name check
    if (value.toLowerCase().includes(initialPasswordState?.firstName?.toLowerCase())
      || value.toLowerCase().includes(initialPasswordState?.lastName?.toLowerCase())
      || value.toLowerCase().includes(initialPasswordState?.email?.toLowerCase())) {

      idNameCheck = false;

      resetPasswordRuleDispatch({
        type: RESET_PASSWORD_RULE_ACTIONS.CHECK_ID_NAME,
        name: "idNameCheck",
        value: false
      });

    } else {
      resetPasswordRuleDispatch({
        type: RESET_PASSWORD_RULE_ACTIONS.CHECK_ID_NAME,
        name: "idNameCheck",
        value: true
      });
    }

    resetPasswordRuleDispatch({
      type: RESET_PASSWORD_RULE_ACTIONS.CHECK_PASSWORD_HISTORY,
      name: "historyCheck",
      value: true
    });

    if (!specialCharFlag) {
      characterCheck = false;
    }

    if (characterCheckValidCount < 3) {
      characterCheck = false;
    }

    //console.log("check password rules", lengthCheck, characterCheck, idNameCheck, countLimitCheck);
    return lengthCheck && characterCheck && idNameCheck && countLimitCheck;

  }

  const handleInputTextChange = (e) => {

    const fieldName = e.target.name;
    const value = e.target.value;

    // trigger rule check and password strength meter for "newPassword"
    if (fieldName == "newPassword") {
      if (checkPasswordRule(value)) {
        // trigger on live password validation for the rule #4
        validateResetPassword(token, value)
          .then((res) => {
            //console.log("handleInputTextChange validate reset password", res, res.data.status);
            if (res !== null && res !== undefined) {
              var statusCode = res.data.status;
              switch (statusCode) {
                case STATUSES.ERROR.code:

                  if (res.data.fieldErrors?.newPassword && res.data.fieldErrors?.newPassword?.errorMessages?.length != 0) {
                    res.data.fieldErrors?.newPassword?.errorMessages.forEach(item => {
                      // set pwd hisotry check flag
                      if (item.includes("4")) {
                        resetPasswordRuleDispatch({
                          type: RESET_PASSWORD_RULE_ACTIONS.CHECK_PASSWORD_HISTORY,
                          name: "historyCheck",
                          value: false
                        });
                      }
                    });
                  };
                  break;
              }
            }
          }).catch(function (error) {
            setGlobalErrorStatus({
              dispatch: dispatch,
              popupData: {
                visible: true,
                title: ERROR_MESSAGES.DATA_ERROR.title,
                msg: ERROR_MESSAGES.DATA_ERROR.message,
                showContactHelp: true
              },
              redirect: {
                redirect: false,
                redirectFirst: false,
                path: "",
                data: {}
              },
              error: error
            });
            return error.response;
          })
      }
    }

    formInfoDispatch({
      type: ACTIONS.TEXT_CHANGE,
      value: value,
      name: fieldName
    });
  }

  const loadFormInfo = (ACTIONS, formInfoDispatch, handleInputTextChange) => {
    let data = {
      password: {
        value: "",
        validation: {
          required: true,
          isInvalid: false,
          errors: []
        },
        onChange: handleInputTextChange,
        readOnly: true,
        disabled: true,
      },
      newPassword: {
        value: "",
        validation: {
          required: true,
          isInvalid: false,
          errors: []
        },
        onChange: handleInputTextChange,
        disabled: false,
      },
      conNewPassword: {
        value: "",
        validation: {
          required: true,
          isInvalid: false,
          errors: []
        },
        onChange: handleInputTextChange,
        disabled: false,
      }
    }

    formInfoDispatch({
      type: ACTIONS.SET_INITIAL_DATA,
      data: data,
      loading: false
    })
  }

  // init validation
  const initValidate = async () => {
    // the Forgot Password Reset Password can only be operated when the user is sign out.
    if (loginState.token) {
      setGlobalErrorStatus({
        dispatch: dispatch,
        popupData: {
          visible: true,
          title: ERROR_MESSAGES.GENERAL_ERROR.title,
          msg: "Please sign out to reset password.",
          showContactHelp: false
        },
        redirect: {
          redirect: false,
          redirectFirst: false,
          path: "",
          data: {}
        }
      });

      return false;
    }

    const res = await validateToken(token)
      .catch(function (error) {
        // unexpected error
        setGlobalErrorStatus({
          dispatch: dispatch,
          popupData: {
            visible: true,
            title: ERROR_MESSAGES.DATA_ERROR.title,
            msg: ERROR_MESSAGES.DATA_ERROR.message,
            showContactHelp: true
          },
          redirect: {
            redirect: false,
            redirectFirst: false,
            path: "",
            data: {}
          },
          error: error
        });
        return error.response;
      });
    // fail the token validation
    if (res && res.data?.status !== 1) {
      setGlobalErrorStatus({
        dispatch: dispatch,
        popupData: {
          visible: true,
          title: ERROR_MESSAGES.DATA_ERROR.title,
          msg: res.data.globalErrorMessageList,
          showContactHelp: false
        },
        redirect: {
          redirect: true,
          redirectFirst: true,
          path: PATHS.public['Sign In'],
          data: {}
        }
      });
      return false;
    }

    if (res && res.data?.status === 1) {
      //forwardPage:1: Reset Password; 2: Setup MFA;   3: Verify MFA 
      if (res.data.data?.forwardPage === 1) {

        return true;
      }
      //console.log("isForward", isForward);
      if (res.data.data?.forwardPage === 3 && isForward !== true) {
        // clear location state
        window.history.replaceState({}, '');
        return false;
      }
    }

    return true;
  }



  // initial loading
  useEffect(() => {

    initValidate().then((isValid) => {

      //console.log("print out initValidate()", isValid);

      // validate token first
      if (!isValid) {
        navigate("/token/" + token);
        return;
      }

      if (isValid) {

        const handleBeforeUnload = () => {
          sessionStorage.setItem('isPageRefreshing', 'true');
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        // Check on component mount if it's a refresh
        if (sessionStorage.getItem('isPageRefreshing') === 'true') {
          //console.log('Page was refreshed!');
          // Clear the flag after detection
          sessionStorage.removeItem('isPageRefreshing');
          navigate("/token/" + token);
        } else {
          //console.log('Page was loaded for the first time in this session.');
          getResetPWDUserInfo(token)
            .then((res) => {
              if (res !== null && res !== undefined) {
                //console.log("getResetPWDUserInfo()", res);
                setInitialPasswordState(res.data?.data);
                var statusCode = res.data.status;
                if (statusCode < 0) {
                  statusCode = STATUSES.ERROR.code;
                }
                switch (statusCode) {
                  case STATUSES.ERROR.code:
                    if (res.data.globalErrorMessageList && res.data.globalErrorMessageList.length != 0) {
                      if (res.data.data?.changedToday === true) {
                        //console.log("res.data.data?.changedToday", res.data.data);
                        resetPasswordRuleDispatch({
                          type: RESET_PASSWORD_RULE_ACTIONS.CHECK_DAILY_CHANGE_COUNT,
                          name: "countLimitCheck",
                          value: !res.data.data.changedToday
                        });
                      }

                      setGlobalErrorStatus({
                        dispatch: dispatch,
                        popupData: {
                          visible: true,
                          title: ERROR_MESSAGES.GENERAL_ERROR.title,
                          msg: res.data.globalErrorMessageList,
                          showContactHelp: false
                        },
                        redirect: {
                          redirect: false,
                          redirectFirst: false,
                          path: "",
                          data: {}
                        }
                      })
                    };
                    break;
                  case STATUSES.SUCCESS.code:
                    resetPasswordRuleDispatch({
                      type: RESET_PASSWORD_RULE_ACTIONS.CHECK_DAILY_CHANGE_COUNT,
                      name: "countLimitCheck",
                      value: !res.data.data.changedToday
                    });
                    break;
                }
              }
            }).catch((error) => {
              setGlobalErrorStatus({
                dispatch: dispatch,
                popupData: {
                  visible: true,
                  title: ERROR_MESSAGES.DATA_ERROR.title,
                  msg: ERROR_MESSAGES.DATA_ERROR.message,
                  showContactHelp: true
                },
                redirect: {
                  redirect: false,
                  redirectFirst: false,
                  path: "",
                  data: {}
                },
                error: error
              });

              return error.response;
            });

          loadFormInfo(ACTIONS, formInfoDispatch, handleInputTextChange);
        }

        return () => {
          window.removeEventListener('beforeunload', handleBeforeUnload);
          sessionStorage.removeItem('isPageRefreshing'); // Clean up
        };
      }
    })
  }, []);

  // refresh state if form state updates
  useEffect(() => {
    loadFormInfo(ACTIONS, formInfoDispatch, handleInputTextChange);
  }, [initialPasswordState]);

  // generate pwd
  const handleGeneratePWD = () => {
    getGeneratePWD(token)
      .then((res) => {
        //console.log("handleGeneratePWD", res);
        var statusCode = res.data.status;
        if (statusCode < 0) {
          statusCode = STATUSES.ERROR.code;
        }
        switch (statusCode) {
          case STATUSES.ERROR.code:
            if (res.data.globalErrorMessageList && res.data.globalErrorMessageList.length != 0) {
              setGlobalErrorStatus({
                dispatch: dispatch,
                popupData: {
                  visible: true,
                  title: ERROR_MESSAGES.GENERAL_ERROR.title,
                  msg: res.data.globalErrorMessageList,
                  showContactHelp: false
                },
                redirect: {
                  redirect: false,
                  redirectFirst: false,
                  path: "",
                  data: {}
                }
              })
            };
            break;
          case STATUSES.SUCCESS.code:
            formInfoDispatch({
              type: ACTIONS.TEXT_CHANGE,
              value: res.data?.data,
              name: "password"
            });
            break;
        }
      }).catch(function (error) {
        setGlobalErrorStatus({
          dispatch: dispatch,
          popupData: {
            visible: true,
            title: ERROR_MESSAGES.DATA_ERROR.title,
            msg: ERROR_MESSAGES.DATA_ERROR.message,
            showContactHelp: true
          },
          redirect: {
            redirect: false,
            redirectFirst: false,
            path: "",
            data: {}
          },
          error: error
        });

        return error.response;
      });
  }

  const handleSubmit = () => {
    // clear error
    setGlobalErrorList([]);
    submitResetPassword(token, formInfo, nameConverter, addErrorMessages, clearErrorMessages, getFieldValue)
      .then((res) => {
        if (res !== null && res !== undefined) {
          //console.log("handleSubmit() submitResetPassword ", res);
          var statusCode = res.data?.status;
          switch (statusCode) {
            case STATUSES.ERROR.code:
              if (res.data.globalErrorMessageList && res.data.globalErrorMessageList.length != 0) {
                setGlobalErrorList(res.data.globalErrorMessageList);
              };
              break;
            case STATUSES.SUCCESS.code:
              // Direct to login page
              navigate(PATHS.public['Sign In'], { state: { message: "Your password was reset successfully." } });
              break;
          }
        }
      }).catch(function (error) {
        setGlobalErrorStatus({
          dispatch: dispatch,
          popupData: {
            visible: true,
            title: ERROR_MESSAGES.DATA_ERROR.title,
            msg: ERROR_MESSAGES.DATA_ERROR.message,
            showContactHelp: true
          },
          redirect: {
            redirect: false,
            redirectFirst: false,
            path: "",
            data: {}
          },
          error: error
        });
        return error.response;
      })
  }
  // back to login
  const handleCancel = () => {

    navigate(PATHS.public['Sign In']);
  }

  const handleCopyPwd = async () => {
    // get the current generated password from form state
    const pwd = getFieldValue("password", formInfo.data);

    if (!pwd) {
      setCopyTooltipStatusMessage("Generate a password first");
      setCopyTooltipStatus('warning');
      renderTooltip();
      return;
    }

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(pwd).then(() => {
          setCopyTooltipStatusMessage("Password copied");
          setCopyTooltipStatus('success');
        }).catch(() => {
          setCopyTooltipStatusMessage("Manual copy required");
          setCopyTooltipStatus('error');
        }).finally(() => renderTooltip());
      }
      else {
        setCopyTooltipStatusMessage("Manual copy required");
        setCopyTooltipStatus('error');
        renderTooltip();
      }
    } catch (error) {
      setGlobalErrorStatus({
        dispatch,
        popupData: {
          visible: true,
          title: ERROR_MESSAGES.DATA_ERROR.title,
          msg: "Could not copy to clipboard. Please copy manually.",
          showContactHelp: false
        },
        redirect: {
          redirect: false,
          redirectFirst: false,
          path: "",
          data: {}
        },
        error
      });
    }
  };

  const hideTooltip = () => {
    setShowCopyTooltip(false);
    setTimeout(() => {
      setCopyTooltipStatusMessage("");
      setCopyTooltipStatus('');
    }, 200);
  };

  const renderTooltip = () => {
    setShowCopyTooltip(true);
  };

  return (
    <>
      {formInfo.loading ? null :
        <Row>
          <Col xl={10} lg={11} md={11} sm={12}>
            <Card className="p-4">
              <Card.Body className="text-start">
                {/* Page title switch based on the user role */}
                <h1 id="mainContent" className="text-start mb-3 blue bold h1-form-title">
                  Reset Password
                </h1>
                {globalErrorList.map((item, index) =>
                  <>
                    <PageInfo key={"errors_" + index} type="error" message={item}></PageInfo>
                    <br />
                  </>
                )}
                <p className="text-start pb-2">
                  To continue the password reset process, enter and confirm a new password.
                  After your password has been reset you will be taken to the sign in page to sign in to your account.
                </p>
                <hr></hr>
                <Form>
                  <Row>
                    <Col lg={7}>
                      <div className="text-start mb-2 passwordRulesTitle">Password Creation Rules</div>
                      <div className="mb-2">
                        <PasswordRuleChecker flag={resetPasswordRule.lengthCheck} content={"1. Contains 16-24 characters"} />
                      </div>
                      <div className="mb-2">
                        <PasswordRuleChecker flag={checkCharacterTypes(resetPasswordRule.characterCheck)} content={"2. Contains 3 of the 4 character types:"} />
                      </div>
                      <ul className="mb-2 ms-1">
                        {passwordTypesList.map(item => {
                          return (
                            <li key={item}>
                              {item}
                            </li>)
                        })}
                      </ul>
                      <div className="mb-2">
                        <PasswordRuleChecker flag={resetPasswordRule.idNameCheck} content={"3. Does not contain the User ID, First Name, or Last Name."} />
                      </div>
                      <div className="mb-2">
                        <PasswordRuleChecker flag={resetPasswordRule.historyCheck} content={"4. Cannot be any password used in the past 12 months."} />
                      </div>
                      <div className="mb-2">
                        <PasswordRuleChecker flag={resetPasswordRule.countLimitCheck} content={"5. Cannot be changed more than once in a 24-hour period."} />
                      </div>
                      <br />
                    </Col>
                    <Col lg={5}>
                      <FormTextInput orgType={"Admin"} fieldName="password" hideLabel={false} horizontalForm={{ is: false, labelCols: { lg: 3, md: 5 }, inputCols: { lg: 9 } }} displayLabelFunc={dispLabel} data={formInfo.data} fieldData={formFieldDisplay} displayErrorsFunc={dispErrors} maxLength={24}></FormTextInput>
                      <Col className="d-flex gap-3">
                        <TextButton
                          variant="normal"
                          //disabled={passedData?.reachedPwdChangeCntMaxToday && !passedData?.psoppcAdmin}
                          onClick={handleGeneratePWD}
                          content={"Generate"}
                        ></TextButton>
                        <TextButton
                          variant="normal"
                          ref={copyButtonRef}
                          //disabled={passedData?.reachedPwdChangeCntMaxToday && !passedData?.psoppcAdmin}
                          onClick={handleCopyPwd}
                          onBlur={hideTooltip}
                          content={"Copy"}
                          title={"Copy to clipboard"}
                        ></TextButton>
                        <StatusOverlay
                          status={copyTooltipStatus}
                          placement="top"
                          target={copyButtonRef}
                          show={showCopyTooltip}
                          message={copyTooltipStatusMessage}
                          arrowMargin={`6px`}>
                        </StatusOverlay>
                      </Col>
                      <br />
                      <Stack gap={0} direction="horizontal" style={{ marginTop: "4px" }}>
                        <Col className="borderLineBreak" lg={4}></Col>
                        <Col className="text-center" lg={1}>or</Col>
                        <Col className="borderLineBreak" lg={4}></Col>
                      </Stack>
                      <br />
                      {/**formInfo, formFieldDisplay, fieldName  */}
                      <PasswordInput fieldName={"newPassword"} formInfo={formInfo} formFieldDisplay={formFieldDisplay} col={9}></PasswordInput>
                      <br />
                      <PasswordStrengthMeter passwordRule={resetPasswordRule} value={getFieldValue("newPassword", formInfo.data)} colConfig={{ labelCols: { lg: 4, md: 4 }, inputCols: { lg: 5 } }}></PasswordStrengthMeter>
                      <PasswordInput fieldName={"conNewPassword"} formInfo={formInfo} formFieldDisplay={formFieldDisplay} col={9}></PasswordInput>
                    </Col>
                  </Row>
                  <Row className="mb-1">
                    <Col>
                      <TextButton
                        className="me-3"
                        variant="normal"
                        disabled={initialPasswordState?.changedToday}
                        onClick={handleSubmit}
                        content={"Submit"}
                      >
                      </TextButton>
                      <TextButton
                        variant="outline"
                        onClick={handleCancel}
                        content={"Cancel"}
                      >
                      </TextButton>
                    </Col>
                  </Row>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      }
    </>
  )
}

export default ForgotPasswordResetPassword;